import { cloneElement, isValidElement } from "react";
import { useTranslation } from "react-i18next";

import type { PerimeterConfig } from "../hooks/usePerimeterSections";
import BrandBadge from "../shared/BrandBadge";
import ContractTypeCheckbox from "../shared/ContractTypeCheckbox";
import ScopeSection from "../shared/ScopeSection";
import styles from "./style.module.scss";

interface PerimetersTabProps {
    permissions: { [key: string]: boolean | any[] }; // All selected permissions
    perimeterOptions: PerimeterConfig[]; // Configuration for which subjects are perimeters
    onPermissionChange: (permissionKey: string, value: boolean | any[]) => void;
    readOnly?: boolean;
}

const PerimetersTab = ({ permissions, perimeterOptions, onPermissionChange, readOnly = false }: PerimetersTabProps) => {
    const { t } = useTranslation();

    /**
     * Generate permission key for a perimeter
     * Format: Subject_action::field_operator (matching permissionMappers.ts)
     */
    const getPermissionKey = (perimeter: PerimeterConfig): string => {
        return `${perimeter.subject}_${perimeter.action}::${perimeter.field}_${perimeter.operator}`;
    };

    /**
     * Get selected option IDs for a perimeter
     * If the permission key doesn't exist (no restriction), all options are selected
     */
    const getSelectedOptions = (perimeter: PerimeterConfig): any[] => {
        const key = getPermissionKey(perimeter);

        // If the permission key doesn't exist, it means no restrictions (all options allowed)
        if (!(key in permissions)) {
            return perimeter.availableOptions.map((opt) => opt.id);
        }

        const value = permissions[key];
        return Array.isArray(value) ? value : [];
    };

    /**
     * Check if a specific perimeter option is selected
     */
    const isOptionSelected = (perimeter: PerimeterConfig, optionId: string): boolean => {
        const selectedOptions = getSelectedOptions(perimeter);
        return selectedOptions.includes(optionId);
    };

    /**
     * Toggle a perimeter option
     */
    const handleOptionToggle = (perimeter: PerimeterConfig, optionId: string) => {
        if (readOnly) return;

        const currentSelected = getSelectedOptions(perimeter);
        const key = getPermissionKey(perimeter);

        let newSelected: any[];
        if (currentSelected.includes(optionId)) {
            // Remove the option
            newSelected = currentSelected.filter((id) => id !== optionId);
        } else {
            // Add the option
            newSelected = [...currentSelected, optionId];
        }

        // Always store as array (even if empty)
        // Empty array = no access (not access to all)
        onPermissionChange(key, newSelected);
    };

    const renderPerimeterOptions = (perimeter: PerimeterConfig) => {
        if (perimeter.displayType === "badge") {
            return (
                <div className={styles["badges-container"]}>
                    {perimeter.availableOptions.map((option) => (
                        <BrandBadge
                            key={option.id}
                            label={option.label}
                            selected={isOptionSelected(perimeter, option.id)}
                            onClick={() => handleOptionToggle(perimeter, option.id)}
                            disabled={readOnly}
                        />
                    ))}
                </div>
            );
        }

        if (perimeter.displayType === "checkbox") {
            return (
                <div className={styles["checkboxes-container"]}>
                    {perimeter.availableOptions.map((option) => (
                        <ContractTypeCheckbox
                            key={option.id}
                            id={option.id}
                            label={option.label}
                            checked={isOptionSelected(perimeter, option.id)}
                            onChange={() => handleOptionToggle(perimeter, option.id)}
                            disabled={readOnly}
                        />
                    ))}
                </div>
            );
        }

        return null;
    };

    return (
        <div className={styles["perimeters-tab"]}>
            {perimeterOptions.map((perimeter, index) => (
                <div key={perimeter.subject}>
                    <ScopeSection
                        icon={
                            isValidElement(perimeter.icon)
                                ? cloneElement(perimeter.icon, {
                                      className: styles["section-icon"],
                                  } as any)
                                : perimeter.icon
                        }
                        label={t(perimeter.labelKey)}
                        description={t(perimeter.descriptionKey)}
                    >
                        {renderPerimeterOptions(perimeter)}
                    </ScopeSection>
                    {index < perimeterOptions.length - 1 && <div className={styles["separator"]} />}
                </div>
            ))}
        </div>
    );
};

export default PerimetersTab;
